import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SensorsIcon from '@mui/icons-material/Sensors';
import DevicesIcon from '@mui/icons-material/Devices';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useAuth } from '../../contexts/AuthContext';
import { Device } from './types';
import { useNavigate } from 'react-router-dom';

interface ChildDevicesModalProps {
  open: boolean;
  onClose: () => void;
  parentDevice: Device;
}

// Map device type IDs to icons
const DEVICE_TYPE_ICONS: Record<string, React.ReactNode> = {
  '111e4567-e89b-12d3-a456-426614174000': <PowerSettingsNewIcon />, // Relay
  // Add camera device type ID when known
  'camera': <VideocamIcon />,
  'sensor': <SensorsIcon />,
};

const getDeviceIcon = (deviceTypeId?: string, deviceTypeName?: string) => {
  if (deviceTypeId && DEVICE_TYPE_ICONS[deviceTypeId]) {
    return DEVICE_TYPE_ICONS[deviceTypeId];
  }
  // Fallback based on name
  const name = deviceTypeName?.toLowerCase() || '';
  if (name.includes('camera')) return <VideocamIcon />;
  if (name.includes('relay')) return <PowerSettingsNewIcon />;
  if (name.includes('sensor')) return <SensorsIcon />;
  return <DevicesIcon />;
};

const ChildDevicesModal: React.FC<ChildDevicesModalProps> = ({ open, onClose, parentDevice }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const navigate = useNavigate();
  const [children, setChildren] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const isAdmin = auth.hasRole('DMO_ADMIN');

  useEffect(() => {
    if (open && parentDevice.id) {
      fetchChildren();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, parentDevice.id]);

  const fetchChildren = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/devices/${parentDevice.id}/children`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setChildren(data);
      } else {
        setError('Failed to fetch child devices');
      }
    } catch (err) {
      setError('An error occurred while fetching child devices');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (deviceId?: string) => {
    if (deviceId) {
      onClose();
      navigate(`/devices/${deviceId}/events`);
    }
  };

  const handleRemoveFromParent = async (child: Device, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!child.id) return;
    
    setRemoving(child.id);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(`/api/devices/${child.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: child.id,
          name: child.name,
          deviceTypeId: child.deviceTypeId,
          parentId: null, // Remove parent
          description: child.description,
          latLong: child.latLong,
        }),
      });
      
      if (response.ok) {
        // Remove from local state
        setChildren(prev => prev.filter(c => c.id !== child.id));
        setSuccess(`${child.name} removed from parent`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to remove device from parent');
      }
    } catch (err) {
      setError('An error occurred while removing device from parent');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DevicesIcon />
          <Typography variant="h6">
            {parentDevice.name} - Child Devices
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && children.length === 0 && (
          <Box textAlign="center" py={4}>
            <DevicesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No child devices found
            </Typography>
          </Box>
        )}

        {!loading && !error && children.length > 0 && (
          <Grid container spacing={2}>
            {children.map((child) => (
              <Grid item xs={12} sm={6} md={4} key={child.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    }
                  }}
                  onClick={() => handleCardClick(child.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box 
                        sx={{ 
                          mr: 2, 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: 40
                        }}
                      >
                        {getDeviceIcon(child.deviceTypeId, child.deviceType?.name)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
                          {child.name}
                        </Typography>
                        {child.deviceType && (
                          <Chip 
                            label={child.deviceType.name} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                    
                    {child.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {child.description}
                      </Typography>
                    )}
                    
                    <Box 
                      sx={{ 
                        bgcolor: 'grey.100',
                        p: 0.5,
                        borderRadius: 1,
                        mt: 1,
                        mb: 2
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        component="div"
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: '0.65rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {child.id}
                      </Typography>
                    </Box>

                    {isAdmin && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Remove from parent">
                          <Button
                            size="small"
                            color="warning"
                            startIcon={<LinkOffIcon />}
                            onClick={(e) => handleRemoveFromParent(child, e)}
                            disabled={removing === child.id}
                          >
                            {removing === child.id ? 'Removing...' : 'Unlink'}
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChildDevicesModal;
